import React, { useContext, useEffect, useMemo } from "react";
import { Feature, MultiPolygon, Polygon } from "geojson";
import { Map } from "mapbox-gl";

import { MapContext } from "fond/map/MapProvider";
import { buildSelectionMask } from "fond/map/selectionMask";
import {
  selectionMaskFillPaint,
  selectionPolygonFillPaint,
  selectionPolygonLineLayout,
  selectionPolygonLinePaint,
} from "fond/map/selectionPolygonStyles";
import { useStyleEpoch } from "fond/map/useStyleEpoch";

export const SELECTION_POLYGON_SOURCE_ID = "selection-polygon";
export const SELECTION_MASK_SOURCE_ID = "selection-mask";
const FILL_LAYER_ID = "selection-polygon-fill";
const LINE_LAYER_ID = "selection-polygon-line";
const MASK_LAYER_ID = "selection-mask-fill";

/**
 * Adds the selection polygon & its mask to the map.
 */
const add = (map: Map, polygon: Feature<Polygon>, mask: Feature<Polygon | MultiPolygon> | null) => {
  // The mask is added first so that the polygon fill & outline draw above it.
  if (mask) {
    map.addSource(SELECTION_MASK_SOURCE_ID, { type: "geojson", data: mask });
    map.addLayer({
      id: MASK_LAYER_ID,
      type: "fill",
      source: SELECTION_MASK_SOURCE_ID,
      paint: selectionMaskFillPaint,
    });
  }

  map.addSource(SELECTION_POLYGON_SOURCE_ID, { type: "geojson", data: polygon });
  map.addLayer({
    id: FILL_LAYER_ID,
    type: "fill",
    source: SELECTION_POLYGON_SOURCE_ID,
    paint: selectionPolygonFillPaint,
  });
  map.addLayer({
    id: LINE_LAYER_ID,
    type: "line",
    source: SELECTION_POLYGON_SOURCE_ID,
    layout: selectionPolygonLineLayout,
    paint: selectionPolygonLinePaint,
  });
};

/**
 * Removes the selection polygon & its mask from the map.
 */
const removeAll = (map: Map) => {
  [MASK_LAYER_ID, FILL_LAYER_ID, LINE_LAYER_ID].forEach((id) => {
    if (map.getLayer(id)) map.removeLayer(id);
  });
  [SELECTION_MASK_SOURCE_ID, SELECTION_POLYGON_SOURCE_ID].forEach((id) => {
    if (map.getSource(id)) map.removeSource(id);
  });
};

interface IProps {
  /**
   * The selection polygon to display.
   */
  polygon: Feature<Polygon>;
}

/**
 * Renders the selection polygon (and dimming mask) on the map.
 */
const SelectionPolygon: React.FC<IProps> = ({ polygon }: IProps) => {
  const { map } = useContext(MapContext);
  const mask = useMemo(() => buildSelectionMask(polygon), [polygon]);
  const styleEpoch = useStyleEpoch(map);

  useEffect(() => {
    if (!map) return undefined;
    add(map, polygon, mask);
    return () => removeAll(map);
  }, [map, polygon, mask, styleEpoch]);

  return null;
};

export default SelectionPolygon;
